class ProductRecommendations extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  observer = undefined;
  isLoading = false;
  isLoaded = false;
  retryCount = 0;
  maxRetries = 3;
  retryDelay = 1000;

  constructor() {
    super();
  }

  connectedCallback() {
    // Vérifier si les données sont déjà disponibles côté serveur
    if (this.hasServerSideRecommendations()) {
      this.handleServerSideContent();
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      console.warn('ProductRecommendations: No product ID provided');
      this.showErrorState('Configuration manquante');
      return;
    }

    this.initializeRecommendations(productId);
  }

  hasServerSideRecommendations() {
    return this.querySelector('.c-push-card__container .c-related-item') !== null;
  }

  handleServerSideContent() {
    this.isLoaded = true;
    this.classList.add('product-recommendations--loaded');
    this.setupAccessibility();
  }

  /**
   * @param {string} productId
   */
  initializeRecommendations(productId) {
    // Nettoyer l'observer existant
    if (this.observer) {
      this.observer.unobserve(this);
    }
    
    // Vérifier si déjà chargé
    if (this.isLoaded) return;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting || this.isLoading || this.isLoaded) return;
        
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { 
        rootMargin: '0px 0px 400px 0px',
        threshold: 0.1
      }
    );
    this.observer.observe(this);
  }

  /**
   * @param {string} productId
   */
  async loadRecommendations(productId) {
    if (this.isLoading || this.isLoaded) return;

    this.isLoading = true;
    this.showLoadingState();

    try {
      const url = `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`;
      const response = await this.fetchWithRetry(url);
      
      if (!response || !response.ok) {
        throw new Error(`HTTP ${response?.status || 'Unknown'}: ${response?.statusText || 'Network Error'}`);
      }

      const text = await response.text();
      this.processRecommendations(text);
      
    } catch (error) {
      console.error('ProductRecommendations: Failed to load recommendations', error);
      this.showErrorState('Impossible de charger les recommandations');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<Response>}
   */
  async fetchWithRetry(url) {
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.ok) {
          this.retryCount = 0; // Reset on success
          return response;
        }
        
        if (attempt === this.maxRetries) {
          return response; // Return last response for error handling
        }
        
      } catch (error) {
        if (attempt === this.maxRetries) {
          throw error;
        }
      }
      
      // Attendre avant de réessayer
      await new Promise(resolve => setTimeout(resolve, this.retryDelay * Math.pow(2, attempt)));
    }
    
    // This should never be reached, but TypeScript needs it
    throw new Error('Max retries exceeded');
  }

  /**
   * @param {string} htmlText
   */
  processRecommendations(htmlText) {
    const html = document.createElement('div');
    html.innerHTML = htmlText;
    const recommendations = html.querySelector('product-recommendations');

    if (!recommendations) {
      this.showEmptyState();
      return;
    }

    const content = recommendations.innerHTML.trim();
    if (!content) {
      this.showEmptyState();
      return;
    }

    // Vérifier la validité du contenu avant injection
    if (this.isValidContent(content)) {
      this.innerHTML = content;
      this.isLoaded = true;
      this.classList.add('product-recommendations--loaded');
      this.setupAccessibility();
      this.announceToScreenReaders('Recommandations chargées');
    } else {
      this.showErrorState('Contenu invalide reçu');
    }
  }

  /**
   * @param {string} content
   * @returns {boolean}
   */
  isValidContent(content) {
    // Vérifications de base pour s'assurer que le contenu est valide
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Vérifier qu'il y a des éléments de produit dans la nouvelle structure
    const hasProducts = tempDiv.querySelector('.c-push-card__container .c-related-item, .product-card, [data-product-id]') !== null;
    const hasValidStructure = tempDiv.querySelector('.c-push-card__container, ul, .grid, .product-grid') !== null;
    
    return hasProducts && hasValidStructure;
  }

  showLoadingState() {
    this.innerHTML = `
      <div class="product-recommendations__loading" role="status" aria-live="polite">
        <div class="loading-spinner" aria-hidden="true"></div>
        <span class="sr-only">Chargement des recommandations...</span>
      </div>
    `;
    this.classList.add('product-recommendations--loading');
  }

  /**
   * @param {string} message
   */
  showErrorState(message) {
    this.innerHTML = `
      <div class="alert alert-danger product-recommendations__error" role="alert">
        <p>${message}</p>
        <button type="button" class="btn btn--secondary" onclick="this.closest('product-recommendations').retry()">
          Réessayer
        </button>
      </div>
    `;
    this.classList.add('product-recommendations--error');
  }

  showEmptyState() {
    this.innerHTML = `
      <div class="product-recommendations__empty" role="status">
        <p>Aucune recommandation disponible pour le moment.</p>
      </div>
    `;
    this.classList.add('product-recommendations--empty');
  }

  retry() {
    this.classList.remove('product-recommendations--error');
    this.retryCount = 0;
    this.isLoaded = false;
    const productId = this.dataset.productId;
    if (productId) {
      this.initializeRecommendations(productId);
    }
  }

  setupAccessibility() {
    // Améliorer l'accessibilité des images
    const images = this.querySelectorAll('img');
    images.forEach(img => {
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
      if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }
      const altText = img.getAttribute('alt');
      if (!altText || altText === '') {
        img.setAttribute('alt', 'Image du produit');
      }
    });

    // Améliorer les liens
    const links = this.querySelectorAll('a[href]');
    links.forEach(link => {
      if (!link.hasAttribute('aria-label') && link.textContent && link.textContent.trim()) {
        link.setAttribute('aria-label', `Voir le produit: ${link.textContent.trim()}`);
      }
    });

    // Ajouter des rôles ARIA appropriés pour la nouvelle structure
    const productContainer = this.querySelector('.c-push-card__container, .grid, .product-grid');
    if (productContainer && !productContainer.hasAttribute('role')) {
      productContainer.setAttribute('role', 'list');
    }

    const productItems = this.querySelectorAll('.c-related-item');
    productItems.forEach(item => {
      if (!item.hasAttribute('role')) {
        item.setAttribute('role', 'listitem');
      }
    });

    // Support pour les layouts spéciaux
    this.setupLayoutSpecificAccessibility();
  }

  setupLayoutSpecificAccessibility() {
    const container = this.querySelector('.c-push-card__container');
    if (!container || !(container instanceof HTMLElement)) return;

    const layout = container.classList.contains('c-push-card__container--slider') ? 'slider' : 
                   container.classList.contains('c-push-card__container--bento') ? 'bento' : 'grid';

    switch (layout) {
      case 'slider':
        this.setupSliderAccessibility(container);
        break;
      case 'grid':
      default:
        this.setupGridAccessibility(container);
        break;
    }
  }

  /**
   * @param {HTMLElement} container
   */
  setupSliderAccessibility(container) {
    // Ajouter des attributs ARIA pour le slider
    container.setAttribute('aria-label', 'Carrousel de produits recommandés');
    container.setAttribute('role', 'region');
    
    // Ajouter des contrôles de navigation si nécessaire
    const items = container.querySelectorAll('.c-related-item');
    items.forEach((item, index) => {
      item.setAttribute('aria-posinset', (index + 1).toString());
      item.setAttribute('aria-setsize', items.length.toString());
    });
  }

  /**
   * @param {HTMLElement} container
   */
  setupGridAccessibility(container) {
    // Configuration standard pour la grille
    container.setAttribute('aria-label', 'Grille de produits recommandés');
  }

  /**
   * @param {string} message
   */
  announceToScreenReaders(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Supprimer après l'annonce
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }

  disconnectedCallback() {
    // Nettoyer l'observer
    if (this.observer) {
      this.observer.unobserve(this);
      this.observer = undefined;
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

